/**
 * Sales order API service (S2.2) — thin typed client over the MSW contract.
 *
 * Pricing reads (`resolveLinePrice`, `listPriceLevels`, `listCustomerPricing`)
 * live here temporarily as SO-entry consumers of the S1.5 pricing endpoints;
 * they migrate to a dedicated `pricing.api.ts` when the W9 定價設定 screens
 * land (see slice followUps).
 */

import apiClient from './axios.config';
import { API_ROUTES } from '@/constants/api.constants';
import type {
  CreateSalesOrderRequest,
  ImportSalesOrdersResponse,
  SalesOrder,
  SalesOrderListParams,
  SalesOrderListResponse,
  UpdateSalesOrderRequest,
} from '@/types/api/salesOrder';
import type {
  CustomerPricing,
  PriceLevel,
  PriceResolution,
  PriceSource,
} from '@/types/api/pricing';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';

export interface ResolveLinePriceParams {
  productId: string;
  customerId?: number | null;
  /** ISO date the resolution is valid AT (defaults to today server-side). */
  date?: string;
  source?: PriceSource;
  qty?: number;
}

export const salesOrderApi = {
  list: async (params?: SalesOrderListParams): Promise<SalesOrderListResponse> => {
    const { data } = await apiClient.get<SalesOrderListResponse>(API_ROUTES.SALES_ORDERS, {
      params,
    });
    return data;
  },

  getById: async (id: string): Promise<{ data: SalesOrder }> => {
    const { data } = await apiClient.get<{ data: SalesOrder }>(`${API_ROUTES.SALES_ORDERS}/${id}`);
    return data;
  },

  create: async (body: CreateSalesOrderRequest): Promise<{ data: SalesOrder }> => {
    const { data } = await apiClient.post<{ data: SalesOrder }>(API_ROUTES.SALES_ORDERS, body);
    return data;
  },

  /** S2.3 modify flow — draft/confirmed only; replaced lines get NEW snapshots. */
  update: async (id: string, body: UpdateSalesOrderRequest): Promise<{ data: SalesOrder }> => {
    const { data } = await apiClient.patch<{ data: SalesOrder }>(
      `${API_ROUTES.SALES_ORDERS}/${id}`,
      body
    );
    return data;
  },

  confirm: async (id: string): Promise<{ data: SalesOrder }> => {
    const { data } = await apiClient.post<{ data: SalesOrder }>(
      `${API_ROUTES.SALES_ORDERS}/${id}/confirm`,
      {}
    );
    return data;
  },

  releasePriceHold: async (id: string, reason: string): Promise<{ data: SalesOrder }> => {
    const { data } = await apiClient.post<{ data: SalesOrder }>(
      `${API_ROUTES.SALES_ORDERS}/${id}/release-price-hold`,
      { reason }
    );
    return data;
  },

  importCsv: async (csv: string): Promise<ImportSalesOrdersResponse> => {
    const { data } = await apiClient.post<ImportSalesOrdersResponse>(
      `${API_ROUTES.SALES_ORDERS}/import`,
      { csv }
    );
    return data;
  },

  createPo: async (
    soId: string,
    body?: { vendorId?: number; productIds?: string[] }
  ): Promise<{ data: PurchaseOrder[]; createdCount: number }> => {
    const { data } = await apiClient.post<{ data: PurchaseOrder[]; createdCount: number }>(
      `${API_ROUTES.SALES_ORDERS}/${soId}/create-po`,
      body || {}
    );
    return data;
  },

  // -------------------------------------------------------------------------
  // Pricing reads (S1.5 endpoints; SO entry is their first consumer)
  // -------------------------------------------------------------------------

  /** The ONE resolve endpoint SO entry (S2.2) and the W9 simulator share. */
  resolveLinePrice: async (params: ResolveLinePriceParams): Promise<{ data: PriceResolution }> => {
    const { data } = await apiClient.get<{ data: PriceResolution }>('/pricing/resolve', {
      params: {
        productId: params.productId,
        ...(params.customerId != null ? { customerId: params.customerId } : {}),
        ...(params.date ? { date: params.date } : {}),
        ...(params.source ? { source: params.source } : {}),
        ...(params.qty != null ? { qty: params.qty } : {}),
      },
    });
    return data;
  },

  listPriceLevels: async (): Promise<{ data: PriceLevel[] }> => {
    const { data } = await apiClient.get<{ data: PriceLevel[] }>('/pricing/levels');
    return data;
  },

  listCustomerPricing: async (customerId: number): Promise<{ data: CustomerPricing[] }> => {
    const { data } = await apiClient.get<{ data: CustomerPricing[] }>('/pricing/customer-pricing', {
      params: { customerId },
    });
    return data;
  },
};

export default salesOrderApi;
